import { isBlockEnabled } from "@/src/lib/blocks/utils";
import { getNestedValue, hasValue } from "@/src/lib/utils/object-utils";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Specialty } from "@/src/models/constants/specialty";
import { type File } from "@/src/models/file";
import { type BlockConfigFile } from "@/src/models/intake/blocks/block-config";
import { BlockKey, BlockType, getBlockTypeFromKey } from "@/src/models/intake/blocks/block-types";
import { getBlockKeysForClassification } from "@/src/models/intake/blocks/enriched-content";
import { PersonGender } from "@/src/models/person";
import { type Section } from "@/src/models/section";

type HasValueAtPath = (path: string) => boolean;

export const AutoIntakeFileCheck = {
  NO_CROPPED_PAGES: {
    failureReason: "cropped_pages",
    passes: (file: Pick<File, "croppedPageIndices"> | null | undefined) =>
      (file?.croppedPageIndices?.length ?? 0) === 0,
  },
  // Set when reception dedupe matched an earlier file and staff did not reject the match.
  NO_VISUAL_DUPLICATE: {
    failureReason: "visual_duplicate",
    passes: (file: Pick<File, "duplicatesFileId"> | null | undefined) => !file?.duplicatesFileId,
  },
} as const;

export type AutoIntakeFileCheck = (typeof AutoIntakeFileCheck)[keyof typeof AutoIntakeFileCheck];

export const AutoIntakeBlockCheck = {
  MATCHED_PROVIDER_NPI: {
    blockType: BlockType.PROVIDER,
    failureReason: "provider_npi_not_matched",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("npiNumber"),
  },
  MATCHED_REFERRING_PROVIDER_NPI: {
    blockType: BlockType.PROVIDER,
    blockKey: BlockKey.REFERRING_PROVIDER,
    failureReason: "referring_provider_npi_not_matched",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("npiNumber"),
  },
  MATCHED_RECEIVING_PROVIDER_NPI: {
    blockType: BlockType.PROVIDER,
    blockKey: BlockKey.RECEIVING_PROVIDER,
    failureReason: "receiving_provider_npi_not_matched",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("npiNumber"),
  },
  GEOCODED_LOCATION: {
    blockType: BlockType.LOCATION,
    failureReason: "location_not_geocoded",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("placeId") && has("address.line1"),
  },
  GEOCODED_REFERRING_LOCATION: {
    blockType: BlockType.LOCATION,
    blockKey: BlockKey.REFERRING_LOCATION,
    failureReason: "referring_location_not_geocoded",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("placeId") && has("address.line1"),
  },
  GEOCODED_RECEIVING_LOCATION: {
    blockType: BlockType.LOCATION,
    blockKey: BlockKey.RECEIVING_LOCATION,
    failureReason: "receiving_location_not_geocoded",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("placeId") && has("address.line1"),
  },
  PATIENT_PHONE_NUMBER: {
    blockType: BlockType.PATIENT,
    failureReason: "phone_number_missing",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) =>
      has("mobilePhoneNumber") || has("homePhoneNumber"),
  },
  PATIENT_HOME_ADDRESS: {
    blockType: BlockType.PATIENT,
    failureReason: "patient_home_address_missing",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("homeAddress.line1"),
  },
  PATIENT_GENDER_KNOWN: {
    blockType: BlockType.PATIENT,
    failureReason: "patient_gender_unknown",
    passes: (has: HasValueAtPath, initialReviewedFields: unknown) =>
      has("gender") && getNestedValue(initialReviewedFields, "gender") !== PersonGender.UNKNOWN,
  },
  // Scoped to primary coverage: secondary coverage is legitimately empty on most documents.
  MATCHED_PRIMARY_COVERAGE_PAYER: {
    blockType: BlockType.COVERAGE,
    blockKey: BlockKey.PRIMARY_COVERAGE,
    failureReason: "primary_coverage_payer_not_matched",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("customerPayer"),
  },
  DETERMINED_IMAGING_PROCEDURE: {
    blockType: BlockType.IMAGING_RESULTS,
    failureReason: "imaging_procedure_missing",
    passes: (has: HasValueAtPath, _initialReviewedFields: unknown) => has("procedure"),
  },
  DETERMINED_SPECIALTY: {
    blockType: BlockType.DOCUMENT_INFORMATION,
    blockKey: BlockKey.DOCUMENT_INFORMATION,
    failureReason: "specialty_unclear",
    passes: (_has: HasValueAtPath, initialReviewedFields: unknown) => {
      const specialties = getNestedValue(initialReviewedFields, "specialties");

      return (
        Array.isArray(specialties) &&
        specialties.length > 0 &&
        specialties.every(
          (specialty) => typeof specialty === "string" && specialty.trim() !== "" && specialty !== Specialty.UNCLEAR
        )
      );
    },
  },
  DETERMINED_SERVICE_LINE: {
    blockType: BlockType.REFERRAL_INFORMATION,
    blockKey: BlockKey.REFERRAL_INFORMATION,
    failureReason: "service_line_not_indicated",
    passes: (_has: HasValueAtPath, initialReviewedFields: unknown) => {
      const serviceLine = getNestedValue(initialReviewedFields, "serviceLine");

      return typeof serviceLine === "string" && serviceLine.trim() !== "" && serviceLine !== ServiceLine.NOT_INDICATED;
    },
  },
  NOT_MARKED_ADW: {
    failureReason: "marked_adw",
    passes: (_has: HasValueAtPath, initialReviewedFields: unknown) =>
      getNestedValue(initialReviewedFields, "isAdw") !== true,
  },
} as const;

export type AutoIntakeBlockCheck = (typeof AutoIntakeBlockCheck)[keyof typeof AutoIntakeBlockCheck];
export type FileEligibilityFailureReason = AutoIntakeFileCheck["failureReason"];
export type BlockEligibilityFailureReason = AutoIntakeBlockCheck["failureReason"];

export function getFileEligibilityFailureReason(
  file: Pick<File, "croppedPageIndices" | "duplicatesFileId"> | null | undefined,
  checks: readonly AutoIntakeFileCheck[]
): FileEligibilityFailureReason | null {
  for (const check of checks) {
    if (!check.passes(file)) {
      return check.failureReason;
    }
  }

  return null;
}

export function getBlockEligibilityFailureReason(
  section: Section,
  blockConfig: BlockConfigFile | undefined | null,
  checks: readonly AutoIntakeBlockCheck[]
): BlockEligibilityFailureReason | null {
  const enrichedContent = section.enrichedContent;

  for (const blockKey of getBlockKeysForClassification(section.classification)) {
    if (!isBlockEnabled(blockConfig, blockKey)) {
      continue;
    }

    const initialReviewedFields = enrichedContent?.[blockKey]?.initialReviewedFields;
    const has = (path: string): boolean => hasValue(getNestedValue(initialReviewedFields, path));
    const blockType = getBlockTypeFromKey(blockKey);

    for (const check of checks) {
      const appliesToBlock =
        (!("blockType" in check) || check.blockType === blockType) &&
        (!("blockKey" in check) || check.blockKey === blockKey);

      if (appliesToBlock && !check.passes(has, initialReviewedFields)) {
        return check.failureReason;
      }
    }
  }

  return null;
}
